import { useCallback, useMemo, useRef } from 'react';
import type { MutableRefObject } from 'react';

/** A scanner types a whole code in well under this; text left alone longer is a leftover, not a scan arriving. */
export const SCAN_SETTLED_MS = 1000;

export interface ScanInputOptions {
  /**
   * Keep the page where the desk left it when the cursor comes back [SSD-7136]. Off by default: a
   * browser scrolls to a focused box, and Daily Closing relies on that to bring its scan box into view.
   */
  preventScroll?: boolean;
}

export interface ScanInputControls {
  ref: MutableRefObject<HTMLInputElement | null>;
  /** Call from the box's onChange, so the box knows a code is still arriving. */
  noteTyping: () => void;
  /** Call once the code in the box was sent: it is a leftover from then on, selected so the next scan replaces it. */
  settle: () => void;
  /** Gives the box the cursor back without ever cutting a code that is still arriving. */
  reclaim: () => void;
}

/**
 * One rule for every way the cursor comes back to a scan box [SSD-6928]. A leftover the desk walked away
 * from is selected, so the next scan replaces it rather than appending to it [G-1]. Text typed within
 * SCAN_SETTLED_MS is the next barcode mid-flight: selecting it made the rest of the code overwrite its
 * head, which is how "93101…" reached the server as "101". A code that was just sent is settled at once
 * [SSD-7135]: it stays on screen, selected, and the next scan replaces it rather than appending to it.
 */
export const useScanInput = ({ preventScroll = false }: ScanInputOptions = {}): ScanInputControls => {
  const ref = useRef<HTMLInputElement | null>(null);
  const lastTypedAt = useRef(0);

  const noteTyping = useCallback(() => {
    lastTypedAt.current = Date.now();
  }, []);

  const settle = useCallback(() => {
    lastTypedAt.current = 0;
    const input = ref.current;
    if (!input) {
      return;
    }
    // A click on Search leaves the cursor on the button, where the next scan would be lost.
    if (document.activeElement !== input) {
      input.focus({ preventScroll });
    }
    input.select();
  }, [preventScroll]);

  const reclaim = useCallback(() => {
    const input = ref.current;
    if (!input) {
      return;
    }
    const hadCursor = document.activeElement === input;
    if (!hadCursor) {
      input.focus({ preventScroll });
    }
    const end = input.value.length;
    if (end > 0 && Date.now() - lastTypedAt.current >= SCAN_SETTLED_MS) {
      input.select();
    } else if (!hadCursor) {
      input.setSelectionRange(end, end);
    }
  }, [preventScroll]);

  // Stable across renders, so an effect can depend on it without firing on every render.
  return useMemo(() => ({ ref, noteTyping, settle, reclaim }), [noteTyping, settle, reclaim]);
};
